import Blacklist from '../blacklist/Blacklist'
import { Device } from '../helpers/Device'
import { domReady, isXAboveY } from '../helpers/DOM'
import WebModel, {
  optimizingForMobilePagespeed,
  optimizingForDesktopPagespeed
} from '../models/WebModel'
import { isElementContainingText, isBlock } from '../dynamic/Dynamic'
import { ContentHints } from '../renderers/ContentHints'
import Logger from '../helpers/Logger'
import { findTargetDivFromScript, insertMissingOptionDivs } from './LegacyVideo'
import {
  setupPlacementForTargetDiv,
  loadDataAndWriteLdJsonForTargetDiv
} from './LoadVideoScripts'
import IVideoConfig from '../../typings/IVideoConfig'

export const MEDIAVINE_VIDEO_URL = 'video.mediavine.com'

export const HOISTED_BODY_CLASS = 'mediavine-video-hoisted'
export const INSERTED_BODY_CLASS = 'mediavine-wrapper-inserted'
export const STICKY_BODY_CLASS = 'mediavine-has-sticky'
export const VIDEO_TARGET_CLASS = 'mv-video-target'

const badTagsToPrecedeVideo = [
  'noscript',
  'script',
  'header',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'img'
]

export default class StickyVideo {
  static hiddenVideo?: HTMLElement
  static videoConfigs: { [key: string]: IVideoConfig } = {}

  constructor(readonly model: WebModel, readonly blacklist: Blacklist) {
    const videoSettings = model.videoSettings
    domReady(() => {
      StickyVideo.setupMvVideoTargets()

      if (
        blacklist.all ||
        window.location.pathname === '/' ||
        !model.videoSettings
      ) {
        return
      }

      if (StickyVideo.hiddenVideo) {
        StickyVideo.hiddenVideo.style.display = ''
        StickyVideo.hiddenVideo = undefined
      }

      // Look for video scripts and div already on the page
      const videosOnPage = StickyVideo.getMediavineVideoTargetDivs()
      const hasVideosOnPage = videosOnPage.length > 0
      const firstVideoTarget = StickyVideo.getFirstValidVideoTargetDiv()
      const hoistLocation = StickyVideo.calculateHoistLocation(this.model)

      if (
        (Device.isMobileOrTablet && model.videoSettings.autoplay_mobile) ||
        (!Device.isMobileOrTablet && model.videoSettings.autoplay_desktop)
      ) {
        if (hasVideosOnPage) {
          if (!firstVideoTarget) {
            // There are video(s) on the page, but none of them are valid to hoist/auto-play
            return
          } else {
            if (StickyVideo.shouldHoistPlayer(firstVideoTarget, model)) {
              StickyVideo.hoistVideo(firstVideoTarget, this.model)
            }
          }
        } else if (
          !blacklist.contains('auto_insert_sticky') &&
          videoSettings.featured_video_type &&
          videoSettings.featured_video_id &&
          hoistLocation
        ) {
          this.insertVideo(
            videoSettings.featured_video_type,
            videoSettings.featured_video_id,
            hoistLocation
          )
        }
      }
    })
  }

  /**
   * This function hides the video div if both "Optimize PageSpeed"
   * AND "Optimize Video Placement" settings are true.
   *
   * This helps us avoid a situation where the publisher has these settings AND
   * has placed their video ATF. In that case the video would appear, and start playing
   * then be repositioned to the "optimized" spot on first scroll. This preemptively
   * hides the video, then unhides it on first-scroll to avoid causing any
   * weirdness to the user.
   *
   * This function should only be called very early on pageview, in the setVideoTargetDivs() flow.
   * @param firstVideo
   * @param model
   */
  static initVideoPlacement(firstVideo: HTMLDivElement, model: IWebViewModel) {
    if (
      !firstVideo ||
      (!optimizingForDesktopPagespeed() && !optimizingForMobilePagespeed())
    ) {
      return
    }

    // We should only hide the video now, if it's going to be hoisted later.
    const shouldHoist = StickyVideo.shouldHoistPlayer(firstVideo, model)
    if (shouldHoist) {
      const contentSelector = !Device.isMobileOrTablet
        ? model.content_selector
        : model.content_selector_mobile || model.content_selector
      const contentDiv = document.querySelector(contentSelector)

      if (contentDiv) {
        firstVideo.style.display = 'none'
        StickyVideo.hiddenVideo = firstVideo
        Logger.debug('Hid first Video to avoid potential ATF jank.')
      }
    }
  }

  /**
   * Given a video slug and hoistLocation, it inserts the video into a page at the correct position.
   * @param featured_video_type
   * @param featured_video_id
   * @param hoistLocation - Location where video should be inserted
   */
  private insertVideo(
    featured_video_type: IVideoSettings['featured_video_type'],
    featured_video_id: IVideoSettings['featured_video_id'],
    hoistLocation: IHoistLocation
  ): void {
    const videoSettings = this.model.videoSettings
    const customHoistSelector = videoSettings.custom_hoist_selector
    const contentElement = getContentElementFromModel(this.model)
    const customHoistElement = customHoistSelector
      ? (document.querySelector(customHoistSelector) as HTMLElement)
      : null

    // We don't want to insert a video if there is a custom_hoist_selector defined, but it is not found on the page
    // or if the hoistLocation could not be computed.
    const customHoistSelectorDefinedAndDoesNotExist =
      customHoistSelector && !customHoistElement
    if (
      (!contentElement && !customHoistElement) ||
      customHoistSelectorDefinedAndDoesNotExist
    ) {
      return
    }

    const { insertTarget, insertPosition } = hoistLocation

    // Insert Video Div
    const videoDiv = this.getVideoTemplateDiv(
      featured_video_type,
      featured_video_id
    )
    insertTarget.insertAdjacentElement(insertPosition, videoDiv)

    // Insert Video Headline
    const videoHeadline = videoSettings.auto_insert_headline
    if (videoHeadline) {
      const headlineElementTag =
        videoSettings.default_video_headline_el ||
        videoSettings.auto_insert_headline_el ||
        'h2'
      const headlineEl = StickyVideo.getHeadlineTemplateDiv(
        videoHeadline,
        headlineElementTag
      )
      videoDiv.insertAdjacentElement('beforebegin', headlineEl)
    }

    setupPlacementForTargetDiv(videoDiv)
  }

  static hoistVideo(videoTarget: HTMLDivElement, model: WebModel) {
    const hoistLocation = StickyVideo.calculateHoistLocation(model)

    if (hoistLocation && videoTarget) {
      const { insertTarget, insertPosition } = hoistLocation
      insertTarget.insertAdjacentElement(insertPosition, videoTarget)
    }
  }

  static shouldHoistPlayer(
    firstVideoTarget: HTMLDivElement,
    model: WebModel | IWebViewModel
  ): boolean {
    // Don't mess with wrapper-inserted video placement
    // Only hoist one video
    // Only hoist a video if there isn't a sticky video on the page
    // Don't hoist a video if it's targetDiv contains "data-disable-optimize=true"
    const stickyVideoAlreadyOnPage =
      document.querySelectorAll(
        'div[data-sticky="true"], div[data-sticky="1"], div[data-sticky=true]'
      ).length === 0

    return (
      !!firstVideoTarget &&
      !isTruthy(firstVideoTarget.getAttribute('data-disable-optimize')) &&
      !isTruthy(firstVideoTarget.getAttribute('data-disable-autoplay')) &&
      model.videoSettings.hoist_first_video &&
      document.getElementsByClassName(HOISTED_BODY_CLASS).length === 0 &&
      document.getElementsByClassName(INSERTED_BODY_CLASS).length === 0 &&
      (firstVideoTarget['autoHoisted'] || stickyVideoAlreadyOnPage)
    )
  }

  // Finds the content ad wrapper or content hint within a contentElement after which we will place the video..
  static getVideoTargetContentAdElement(contentElement: HTMLElement | null) {
    let adWrapper, adWrappers
    if (Device.isMobileOrTablet) {
      adWrappers =
        contentElement &&
        contentElement.querySelectorAll(
          `.content_mobile_wrapper,${ContentHints.MOBILE_SELECTOR}`
        )
      adWrapper = adWrappers && adWrappers.length > 1 && adWrappers[1]
    } else {
      adWrappers =
        contentElement &&
        contentElement.querySelectorAll(
          `.content_btf_wrapper,${ContentHints.DESKTOP_SELECTOR}`
        )
      adWrapper = adWrappers && adWrappers.length > 0 && adWrappers[0]
    }

    // The wrapper might be inside a waypoint, or it might be inside a `remove_padding` div.
    // Let's climb up the DOM tree until we find an element whose parent has more than one child.
    while (
      adWrapper &&
      adWrapper.parentElement !== contentElement &&
      adWrapper.parentElement.children.length < 2
    ) {
      adWrapper = adWrapper.parentElement
    }

    return adWrapper
  }

  static canPlaceAdAfterElement(element: HTMLElement) {
    return (
      StickyVideo.isTwoConsecutiveBRs(element) ||
      (isElementContainingText(element) &&
        isBlock(element) &&
        badTagsToPrecedeVideo.indexOf(element.tagName.toLowerCase()) === -1)
    )
  }

  /**
   * Given an element, returns true if it is a <BR> AND if the nextElementSibling
   * is also a <BR>, AND there is nothing but whitespace between the two elements,
   * AND if the element's previousSibling is OK to put a video after.
   *
   * @param element - HTMLElement
   */
  static isTwoConsecutiveBRs(element: HTMLElement): boolean {
    let betweenBrs = false
    if (element.tagName === 'BR') {
      const nextElementTag =
        element.nextElementSibling && element.nextElementSibling.tagName
      const prevTag =
        element.previousElementSibling && element.previousElementSibling.tagName

      const consecutiveBRs = nextElementTag === 'BR'

      if (consecutiveBRs && prevTag) {
        const nextSibling = element.nextSibling
        const nextSiblingTag = nextSibling && nextSibling.nodeName
        const nextSiblingText =
          nextSibling && (nextSibling.textContent || '').trim()

        // If there is a #text node between the two <BR>s, check to make sure it is only white-space.
        const noTextBetweenBRs = !!(
          element.nextSibling &&
          (nextSiblingTag === 'BR' ||
            (nextSiblingTag === '#text' && nextSiblingText === ''))
        )
        // Check to see if the tag before the first BR is ok to place an ad after.
        betweenBrs =
          noTextBetweenBRs &&
          badTagsToPrecedeVideo.indexOf(prevTag.toLowerCase()) === -1
      }
    }
    return betweenBrs
  }

  static calculateHoistLocation(model: WebModel): IHoistLocation | undefined {
    let insertPosition
    let insertTarget

    const contentElement = getContentElementFromModel(model)
    const customHoistElement = model.videoSettings.custom_hoist_selector
      ? (document.querySelector(
          model.videoSettings.custom_hoist_selector
        ) as HTMLElement)
      : null
    const customHoistPosition =
      (model.videoSettings.custom_hoist_position as InsertPosition) || undefined

    const targetContentAd = StickyVideo.getVideoTargetContentAdElement(
      contentElement
    )

    if (customHoistElement) {
      insertTarget = customHoistElement
      insertPosition = customHoistPosition || 'afterbegin'

      return { insertTarget, insertPosition }
    }

    if (
      contentElement instanceof HTMLElement &&
      targetContentAd instanceof HTMLElement
    ) {
      let nextTextSibling = targetContentAd.nextElementSibling as HTMLElement

      // Find a valid element after targetContentAd to place the video after.
      while (
        nextTextSibling &&
        !StickyVideo.canPlaceAdAfterElement(nextTextSibling)
      ) {
        nextTextSibling = nextTextSibling.nextElementSibling as HTMLElement
      }

      // If the next element is just too dang tall, place the video after the ad, instead of after the element.
      // This will prevent the video from being place too far down the page.
      const heightPercentageThreshold = 0.3
      const nextSiblingIsTooTall =
        nextTextSibling &&
        contentElement.offsetHeight > 0 &&
        nextTextSibling.offsetHeight / contentElement.offsetHeight >
          heightPercentageThreshold

      if (nextTextSibling && !nextSiblingIsTooTall) {
        insertTarget = nextTextSibling
        insertPosition = 'afterend'
      } else {
        // If there are no valid elements after the content_btf_waypoint, place it directly after the ad.
        insertTarget = targetContentAd
        insertPosition = 'afterend'
      }
    } else if (contentElement instanceof HTMLElement) {
      // contentBtfDiv doesn't exist, probably because they have turned off in-content ads
      // or maybe their content is way too short.
      // Shove the video after the 5th element and hope for the best!

      const videoOffset = 4 // zero-indexed
      // Make sure that there are enough children for videoOffset,
      // if not, insert before the last child.
      const contentIndex =
        contentElement.children.length >= videoOffset
          ? videoOffset
          : contentElement.children.length - 1

      if (contentIndex > 0) {
        insertTarget = contentElement.children[contentIndex]
        insertPosition = 'afterend'
      } else {
        insertTarget = contentElement
        insertPosition = 'afterbegin'
      }
    }

    if (!insertTarget) {
      return undefined
    }

    // We need to place the video above the create card if the crete card is high enough
    // on the page that the first content-ad is inside the create card.
    const createCard = document.querySelector(
      '.wp-block-mv-list'
    ) as HTMLElement
    if (createCard && isXAboveY(createCard, insertTarget)) {
      insertTarget = createCard
      insertPosition = 'beforebegin'
    }

    return { insertTarget, insertPosition }
  }

  private getVideoTemplateDiv(
    featured_video_type: IVideoSettings['featured_video_type'],
    featured_video_id: IVideoSettings['featured_video_id']
  ): HTMLDivElement {
    const div = document.createElement('div')
    div.className = VIDEO_TARGET_CLASS
    div.setAttribute(`data-${featured_video_type}-id`, featured_video_id)
    div.setAttribute('data-autoplay', 'true')
    div.setAttribute('data-automobile', 'true')
    div.setAttribute('data-muted', 'true')
    div.setAttribute('data-sticky', 'true')
    div.setAttribute('data-autoinsert', 'true')
    div.setAttribute('data-disable-jsonld', 'true')

    return div
  }

  static getHeadlineTemplateDiv(
    headline: string,
    headlineElTag: string
  ): HTMLElement {
    const headlineEl = document.createElement(headlineElTag)
    headlineEl.className = 'mv_auto_insert_headline'
    headlineEl.textContent = headline

    return headlineEl
  }

  /**
   * It inserts videos into preexisting `.mv-video-target` divs
   */
  static async setupMvVideoTargets() {
    const selector = `div.${VIDEO_TARGET_CLASS}`
    const divs = document.querySelectorAll(selector) as NodeListOf<
      HTMLDivElement
    >

    const validTargetDivs = Array.from(divs).filter((div) => {
      return (
        StickyVideo.getVideoIdFromTargetDiv(div) ||
        StickyVideo.getPlaylistIdFromTargetDiv(div)
      )
    })

    // Setup Placement for each target div.
    for (const div of validTargetDivs) {
      await setupPlacementForTargetDiv(div)
    }
  }

  /**
   * Because Wordpress is stupid and allows unknown attributes to be stripped from elements,
   * we need to have a secondary way of specifying the video or playlist id on a target div.
   *
   * This function will attempt the primary method (the `data-video-id=[video_id]` attribute)
   * and fall back to the secondary method (the `mv-video-id-[video_id]` class) if that fails.
   * @param targetDiv
   */
  static getVideoIdFromTargetDiv(targetDiv: HTMLDivElement): string | null {
    if (targetDiv.hasAttribute('data-video-id')) {
      return targetDiv.getAttribute('data-video-id')
    } else {
      // find mv-video-id-[video_id] class, and extract the video id from it.
      const classList = Array.from(targetDiv.classList)
      const videoIdClass = classList.find((name) => {
        return name.indexOf('mv-video-id-') !== -1
      })
      if (videoIdClass) {
        return videoIdClass.split('-')[3]
      } else {
        return null
      }
    }
  }

  static getPlaylistIdFromTargetDiv(targetDiv: HTMLDivElement): string | null {
    if (targetDiv.hasAttribute('data-playlist-id')) {
      return targetDiv.getAttribute('data-playlist-id')
    } else {
      // find mv-playlist-id-[video_id] class, and extract the playlist id from it.
      const classList = Array.from(targetDiv.classList)
      const videoIdClass = classList.find((name) => {
        return name.indexOf('mv-playlist-id-') !== -1
      })
      if (videoIdClass) {
        return videoIdClass.split('-')[3]
      } else {
        return null
      }
    }
  }

  static getMediavineVideoTargetDivs(): Array<HTMLDivElement> {
    const selector = `div.${VIDEO_TARGET_CLASS}`

    const elements = document.querySelectorAll(selector) as NodeListOf<
      HTMLScriptElement | HTMLDivElement
    >

    const targetDivs: HTMLDivElement[] = []
    // get all the target divs on the page!
    Array.from(elements).forEach((element) => {
      if (element.tagName === 'DIV') {
        targetDivs.push(element as HTMLDivElement)
      } else {
        const targetDiv = findTargetDivFromScript(element as HTMLScriptElement)
        if (targetDiv) {
          targetDivs.push(targetDiv)
        }
      }
    })

    return targetDivs
  }

  // Finds the first Mediavine video target div element that exists on the page.
  static getFirstValidVideoTargetDiv(): HTMLDivElement | undefined {
    const videoTargets = StickyVideo.getMediavineVideoTargetDivs()
    return videoTargets.find((targetDiv) => {
      return (
        !targetDiv ||
        (!isTruthy(targetDiv.getAttribute('data-disable-auto-upgrade')) &&
          !isTruthy(targetDiv.getAttribute('data-disable-autoplay')))
      )
    })
  }

  static insertVideoHeadline(
    targetDiv: HTMLDivElement,
    videoSettings: IVideoSettings
  ): void {
    const videoId = this.getVideoIdFromTargetDiv(targetDiv)
    const playlistId = this.getPlaylistIdFromTargetDiv(targetDiv)
    const disableHeadline = isTruthy(
      targetDiv.getAttribute('data-disable-headline')
    )
    const customHeadline = targetDiv.getAttribute('data-video-headline')

    if (disableHeadline) {
      return
    }

    if (videoId || playlistId) {
      let headline
      if (videoId) {
        const videoConfig = StickyVideo.videoConfigs[videoId]
        headline =
          customHeadline ||
          videoConfig.video.videoHeadline ||
          videoSettings.default_video_headline
      } else {
        headline = customHeadline || videoSettings.default_video_headline
      }

      const headlineTag = videoSettings.default_video_headline_el || 'h2'

      if (headline) {
        const headlineEl = StickyVideo.getHeadlineTemplateDiv(
          headline,
          headlineTag
        )
        targetDiv.insertAdjacentElement('beforebegin', headlineEl)
      }
    }
  }

  // This will be called as one of the first things web-wrapper does to avoid
  // a race condition that prevents videos from using these options.
  static setVideoTargetDivs(model: IWebViewModel) {
    insertMissingOptionDivs()

    const targetDivs = StickyVideo.getMediavineVideoTargetDivs()
    targetDivs.forEach((targetDiv) => {
      loadDataAndWriteLdJsonForTargetDiv(targetDiv)
    })

    const firstVideo = StickyVideo.getFirstValidVideoTargetDiv()
    if (firstVideo) {
      StickyVideo.setAutoplayOptions(firstVideo, model)
      StickyVideo.initVideoPlacement(firstVideo, model)
    }
  }

  static setAutoplayOptions(
    firstVideoDiv: HTMLDivElement,
    model: IWebViewModel | WebModel
  ) {
    const otherStickyPlayerOnPage =
      document.querySelectorAll(
        'div[data-sticky="true"], div[data-sticky="1"], div[data-sticky=true]'
      ).length > 0

    if (
      (Device.isMobileOrTablet && model.videoSettings.autoplay_mobile) ||
      (!Device.isMobileOrTablet && model.videoSettings.autoplay_desktop)
    ) {
      if (firstVideoDiv) {
        firstVideoDiv.setAttribute('data-automobile', 'true')
        firstVideoDiv.setAttribute('data-autoplay', 'true')
        firstVideoDiv.setAttribute('data-muted', 'true')
        if (!otherStickyPlayerOnPage) {
          firstVideoDiv.setAttribute('data-sticky', 'true')
          // This is a bit hacky, but it lets us know later if this
          // video should actually be hoisted or not.
          firstVideoDiv['autoHoisted'] = true
        }
      } else {
        Logger.debug('Video: Something has gone wrong setting video options.')
      }
    }
  }
}

/**
 * Finds the device-appropriate contentElement, given a WebModel.
 *
 * @param model
 */
function getContentElementFromModel(model: WebModel): HTMLElement | null {
  let contentElement

  if (Device.isMobileOrTablet && model.content_selector_mobile) {
    contentElement = document.querySelector(
      model.content_selector_mobile
    ) as HTMLElement
  } else {
    contentElement = model.content_selector
      ? (document.querySelector(model.content_selector) as HTMLElement)
      : null
  }

  return contentElement
}

function isTruthy(value: any): boolean {
  return ['1', 'true', true, 1].indexOf(value) !== -1
}

interface IHoistLocation {
  insertTarget: HTMLElement
  insertPosition: InsertPosition
}
